import { useEffect, useRef } from 'react';
import { useTranslations } from 'use-intl';
import { ArrowUp, Square } from 'lucide-react';

/**
 * Where the question is typed, pinned to the bottom of the conversation.
 *
 * It grows with what is in it, up to a point. A fixed three-row box is too tall
 * for "who plays left back?" and too short for a question worth asking, and the
 * one thing it must never do is push itself off the screen.
 */
export function Composer({
  value,
  onChange,
  onSend,
  onStop,
  busy,
  hint,
}: {
  value: string;
  onChange: (next: string) => void;
  onSend: () => void;
  /** Stops the answer. The turn is already recorded and charged server-side. */
  onStop: () => void;
  busy: boolean;
  hint: string;
}) {
  const t = useTranslations();
  const box = useRef<HTMLTextAreaElement>(null);
  const ready = value.trim().length >= 2 && !busy;

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    // Reset first: a textarea's scrollHeight never shrinks on its own, so
    // measuring without this makes the box a one-way ratchet.
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [value]);

  return (
    <div className="border-t border-line bg-surface p-3">
      <div className="flex items-end gap-2 rounded-(--radius-panel) border border-line bg-surface px-3 py-2 transition-colors focus-within:border-ink">
        <label className="sr-only" htmlFor="chat-composer">
          {t('chat.placeholder')}
        </label>
        <textarea
          id="chat-composer"
          ref={box}
          rows={1}
          value={value}
          placeholder={t('chat.placeholder')}
          maxLength={2000}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter is a new line. A coach typing one
            // question does not want to reach for a button.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (ready) onSend();
            }
          }}
          className="max-h-[180px] min-h-11 flex-1 resize-none bg-transparent py-2 leading-relaxed text-ink placeholder:text-ink-faint focus:outline-none"
        />
        {/* While an answer is being written the same place offers to stop it,
            which is where every assistant worth copying puts it. */}
        {busy ? (
          <button
            type="button"
            onClick={onStop}
            aria-label={t('chat.stop')}
            className="mb-0.5 grid size-11 shrink-0 place-items-center rounded-full bg-ink text-paper transition-colors hover:bg-ink/90"
          >
            <Square aria-hidden="true" className="size-4 fill-current" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onSend}
            disabled={!ready}
            aria-label={t('chat.send')}
            className="mb-0.5 grid size-11 shrink-0 place-items-center rounded-full bg-ink text-paper transition-opacity disabled:opacity-30"
          >
            <ArrowUp aria-hidden="true" className="size-5" />
          </button>
        )}
      </div>
      <p className="mt-2 px-1 text-meta text-ink-faint">{hint}</p>
    </div>
  );
}
